import {
	ShipmentLocationActionDtoLocationActionType,
	useFetchUturnUserById,
} from '@uturn/api/v1';
import { useFetchAllPreferredCarrierWithGroups } from '@uturn/api/v2';
import { Permission } from '@uturn/portal/abac';
import { MetaDataContext } from '@uturn/portal/context';
import { isSelectableShipmentRequirement } from '@uturn/portal/types/shipment-requirements.typeguard';
import { useContext, useMemo } from 'react';
import { useAbac } from 'react-abac';

const useShipmentTableOptions = () => {
	const { userHasPermissions } = useAbac();
	const { shipmentMetaData, metadata } = useContext(MetaDataContext);
	const userId = metadata?.user?.id;

	// The user's prefilled values ("Edit defaults"), used to seed every row.
	const { data: userSettings, isLoading: isUserLoading } =
		useFetchUturnUserById(userId ?? -1, {
			query: {
				enabled: userId !== undefined,
				select: (data) => data.data.settings,
			},
		});

	const { data: carriers, isLoading: isCarriersLoading } =
		useFetchAllPreferredCarrierWithGroups({
			query: {
				enabled: userHasPermissions(Permission.INVITE_CARRIER),
				select: (data) => [
					...(data.data.carrierGroup?.map((group) => ({
						value: group.groupId,
						name: group.groupName,
						options: group.carriers.map((carrier) => ({
							value: carrier.organizationId,
							label: carrier.organizationName,
						})),
					})) ?? []),
					...(data.data.individualCarriers?.map((carrier) => ({
						value: carrier.organizationId,
						label: carrier.organizationName,
					})) ?? []),
				],
			},
		});
	const shippingLines = useMemo(
		() =>
			shipmentMetaData?.shippingLines
				?.filter(
					(shippingLine) =>
						!!shippingLine.id &&
						!!shippingLine.code &&
						!!shippingLine.description,
				)
				.map((shippingLine) => ({
					value: (shippingLine.id as number).toString(), // Guarantied by Type Predicate Inference
					label: `${shippingLine.description} (${shippingLine.code})`,
					scacCode: shippingLine.code,
				})) ?? [],
		[shipmentMetaData?.shippingLines],
	);
	const containerTypes = useMemo(
		() =>
			shipmentMetaData?.shipmentUnits
				?.filter(
					(shipmentUnit) =>
						!!shipmentUnit.id &&
						!!shipmentUnit.portbaseCode &&
						!!shipmentUnit.description,
				)
				.map((shipmentUnit) => ({
					value: (shipmentUnit.id as number).toString(), // Guarantied by Type Predicate Inference
					label: shipmentUnit.description as string, // Guarantied by Type Predicate Inference
					isoCode: shipmentUnit.portbaseCode as string, // Guarantied by Type Predicate Inference
				})) ?? [],
		[shipmentMetaData?.shipmentUnits],
	);
	const requirements = useMemo(
		() =>
			shipmentMetaData?.shipmentRequirements
				?.filter(isSelectableShipmentRequirement)
				.map((shipmentRequirement) => ({
					value: shipmentRequirement.id,
					label: shipmentRequirement.description,
				})) ?? [],
		[shipmentMetaData?.shipmentRequirements],
	);
	const locationTypes = useMemo(
		() =>
			Object.keys(ShipmentLocationActionDtoLocationActionType)
				.filter((locationType) => locationType !== '')
				.map((locationType) => ({
					value: locationType.toLowerCase(),
					label: locationType,
				})) ?? [],
		[],
	);
	const termsAndConditions = [
		{
			value: 'avc',
			label: 'AVC',
		},
		{
			value: 'cmr',
			label: 'CMR',
		},
		{
			value: 'rha',
			label: 'RHA',
		},
	];

	return {
		isPending:
			isCarriersLoading || isUserLoading || shipmentMetaData === undefined,
		containerTypes,
		shippingLines,
		requirements,
		locationTypes,
		termsAndConditions,
		carriers,
		userSettings,
	};
};

export default useShipmentTableOptions;
